import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Menu, X, Phone } from "lucide-react";

export default function SiteHeader({ logoUrl, siteName = "Cuba Bella Café", phone = "+1 (972) 710-3322" }) {
    const { t, lang, toggle } = useI18n();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const navItems = [
        { href: "#hero", label: t("nav_home"), testid: "nav-home" },
        { href: "#menu", label: t("nav_menu"), testid: "nav-menu" },
        { href: "#story", label: t("nav_story"), testid: "nav-story" },
        { href: "#gallery", label: t("nav_gallery"), testid: "nav-gallery" },
        { href: "#contact", label: t("nav_contact"), testid: "nav-contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
                scrolled ? "glass-panel" : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-5 md:px-10 h-20 flex items-center justify-between">
                <a href="#hero" data-testid="header-logo-link" className="flex items-center gap-3">
                    {logoUrl ? (
                        <img
                            src={logoUrl}
                            alt={siteName}
                            data-testid="header-logo-img"
                            className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover ring-1 ring-gold/40"
                        />
                    ) : (
                        <div className="h-12 w-12 rounded-full bg-gold/20 border border-gold/30" />
                    )}
                    <div className="hidden sm:flex flex-col leading-tight">
                        <span className="font-serif italic text-gold text-[0.9rem] tracking-widest uppercase">
                            Habana
                        </span>
                        <span className="font-serif text-white text-lg tracking-tight">
                            {siteName}
                        </span>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {navItems.map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            data-testid={n.testid}
                            className="text-sm tracking-[0.14em] uppercase text-habana-cream/75 hover:text-gold transition-colors duration-200"
                        >
                            {n.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3 md:gap-5">
                    <a
                        href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                        data-testid="header-phone-link"
                        className="hidden md:flex items-center gap-2 text-sm text-habana-cream/80 hover:text-gold transition-colors"
                    >
                        <Phone size={14} strokeWidth={1.8} />
                        <span className="tracking-wide">{phone}</span>
                    </a>
                    <button
                        onClick={toggle}
                        data-testid="lang-toggle"
                        className="hidden md:flex items-center px-3 py-1.5 rounded-full border border-gold/40 text-xs tracking-widest uppercase text-gold hover:bg-gold hover:text-black transition-colors"
                        aria-label="Toggle language"
                    >
                        {lang === "es" ? "EN" : "ES"}
                    </button>
                    <a href="#contact" className="hidden md:inline-flex btn-ghost-gold" data-testid="header-reserve-cta">
                        {t("cta_reserve")}
                    </a>
                    <button
                        className="lg:hidden text-habana-cream"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden glass-panel border-t border-gold/10" data-testid="mobile-menu-panel">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {navItems.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                onClick={() => setOpen(false)}
                                data-testid={`m-${n.testid}`}
                                className="text-lg text-habana-cream/85 font-serif"
                            >
                                {n.label}
                            </a>
                        ))}
                        <div className="flex items-center gap-4 pt-3 border-t border-gold/10">
                            <button
                                onClick={toggle}
                                data-testid="m-lang-toggle"
                                className="px-3 py-1.5 rounded-full border border-gold/40 text-xs tracking-widest uppercase text-gold"
                            >
                                {lang === "es" ? "EN" : "ES"}
                            </button>
                            <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="text-sm text-habana-cream/85">
                                {phone}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
